import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { createIngredient, searchIngredients } from "../../utils/apiClient";
import { INGREDIENT_UNITS } from "../../post/compositions";

function emptyItem() {
  return {
    ingredient_uuid: null,
    ingredient_name: "",
    amount: "",
    unit: "gram",
    note: "",
  };
}

function EditIngredientGroupModal({
  open,
  onClose,
  mode = "add",
  initial = null,
  onSaved,
  onDeleted,
}) {
  const [name, setName] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [optionsByIndex, setOptionsByIndex] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setBusy(false);
    setOptionsByIndex({});
    if (mode === "edit" && initial) {
      setName(initial.name || "");
      setItems(
        (initial.items || []).length
          ? initial.items.map((item) => ({
              ingredient_uuid: item.ingredient_uuid || null,
              ingredient_name: item.ingredient_name || "",
              amount: item.amount ?? "",
              unit: item.unit || "gram",
              note: item.note || "",
            }))
          : [emptyItem()]
      );
    } else {
      setName("");
      setItems([emptyItem()]);
    }
  }, [open, mode, initial]);

  const updateItem = (index, patch) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const handleIngredientInput = async (index, text) => {
    updateItem(index, { ingredient_name: text, ingredient_uuid: null });
    if (!text.trim()) {
      setOptionsByIndex((prev) => ({ ...prev, [index]: [] }));
      return;
    }
    try {
      const results = await searchIngredients(text.trim());
      setOptionsByIndex((prev) => ({ ...prev, [index]: results || [] }));
    } catch {
      setOptionsByIndex((prev) => ({ ...prev, [index]: [] }));
    }
  };

  const resolveIngredient = async (index, text) => {
    const cleaned = (text || "").trim();
    if (!cleaned) return;
    setBusy(true);
    try {
      const created = await createIngredient(cleaned);
      updateItem(index, {
        ingredient_uuid: created.ingredient_uuid,
        ingredient_name: created.name,
      });
    } catch {
      updateItem(index, { ingredient_name: cleaned });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    const cleanedName = name.trim();
    if (!cleanedName) {
      setError("Tilføj et gruppenavn.");
      return;
    }
    const cleanedItems = items
      .filter((item) => (item.ingredient_name || "").trim() && item.amount !== "")
      .map((item) => ({
        ingredient_uuid: item.ingredient_uuid || null,
        ingredient_name: (item.ingredient_name || "").trim(),
        amount: Number(item.amount),
        unit: item.unit || "gram",
        note: (item.note || "").trim(),
      }));
    if (!cleanedItems.length) {
      setError("Tilføj mindst én ingrediens med en mængde.");
      return;
    }
    for (const item of cleanedItems) {
      if (Number.isNaN(item.amount) || item.amount < 0) {
        setError("Mængder skal være gyldige, ikke-negative tal.");
        return;
      }
    }
    onSaved?.({
      groupUuid: initial?.groupUuid || crypto.randomUUID(),
      name: cleanedName,
      items: cleanedItems,
    });
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {mode === "edit" ? "Rediger ingrediensgruppe" : "Tilføj ingrediensgruppe"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField
            label="Gruppenavn"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            disabled={busy}
            placeholder="f.eks. Dej"
          />
          {items.map((item, index) => {
            const options = optionsByIndex[index] || [];
            return (
              <Stack
                key={index}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "flex-start" }}
              >
                <Autocomplete
                  freeSolo
                  options={options}
                  getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.name || "")}
                  inputValue={item.ingredient_name || ""}
                  onInputChange={(_, text, reason) => {
                    if (reason === "input" || reason === "clear") {
                      handleIngredientInput(index, text);
                    }
                  }}
                  onChange={async (_, selected) => {
                    if (!selected) {
                      updateItem(index, { ingredient_uuid: null, ingredient_name: "" });
                      return;
                    }
                    if (typeof selected === "string") {
                      await resolveIngredient(index, selected);
                      return;
                    }
                    updateItem(index, {
                      ingredient_uuid: selected.ingredient_uuid,
                      ingredient_name: selected.name,
                    });
                  }}
                  onBlur={() => {
                    if (item.ingredient_name && !item.ingredient_uuid) {
                      resolveIngredient(index, item.ingredient_name);
                    }
                  }}
                  disabled={busy}
                  sx={{ flex: 2, minWidth: 160 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Ingrediens" size="small" />
                  )}
                />
                <TextField
                  label="Mængde"
                  value={item.amount}
                  onChange={(e) => updateItem(index, { amount: e.target.value })}
                  size="small"
                  type="number"
                  inputProps={{ step: "any", min: 0 }}
                  disabled={busy}
                  sx={{ width: { xs: "100%", sm: 100 } }}
                />
                <TextField
                  select
                  label="Enhed"
                  value={item.unit || "gram"}
                  onChange={(e) => updateItem(index, { unit: e.target.value })}
                  size="small"
                  disabled={busy}
                  sx={{ width: { xs: "100%", sm: 110 } }}
                >
                  {INGREDIENT_UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Note"
                  value={item.note || ""}
                  onChange={(e) => updateItem(index, { note: e.target.value })}
                  size="small"
                  disabled={busy}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <IconButton
                  aria-label="Fjern ingrediens"
                  disabled={busy || items.length <= 1}
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={busy}
            onClick={() => setItems((prev) => [...prev, emptyItem()])}
            sx={{ alignSelf: "flex-start" }}
          >
            Tilføj ingrediens
          </Button>
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        {mode === "edit" ? (
          <Button
            color="error"
            disabled={busy}
            onClick={() => {
              if (!window.confirm("Fjern denne ingrediensgruppe fra opskriften?")) return;
              onDeleted?.();
              onClose?.();
            }}
          >
            Slet
          </Button>
        ) : (
          <span />
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={busy}>
            Annuller
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={busy}>
            {mode === "edit" ? "Gem" : "Tilføj til opskrift"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditIngredientGroupModal;
